import React, { useState, useEffect } from "react";

import moment from "moment";
import DateFnsUtils from "@date-io/date-fns";

import {
    Checkbox,
    TextField,
    InputAdornment,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    IconButton,
    Input
} from "@material-ui/core";
import {
    MuiPickersUtilsProvider,
    DatePicker,
    KeyboardDatePicker,
    KeyboardTimePicker
} from "@material-ui/pickers";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";

import ReactSelectForm from "./ReactSelect";
import Tooltip from "../Tooltip";

import "./index.css";

export const textField = ({
    field,
    field: { name },
    form: {
        setFieldValue ,
        touched,
        errors
    },
    enableAndorment,
    textAdornment,
    onChange,
    ...props
}) => (
    <>
        <TextField
            {...field}
            {...props}
            fullWidth
            InputProps={!enableAndorment ? null : {
                endAdornment: (
                    <InputAdornment position="start">
                        {textAdornment}
                    </InputAdornment>
                )
            }}
            onChange={e => {
                setFieldValue(name, e.target.value);
                onChange && onChange(e.target.value, setFieldValue);
            }}
        />

        {touched[field.name] && errors[field.name] && (
            <small className="handle-text-error handle-error-animation">
                {errors[field.name]}
            </small>
        )}
    </>
);

export const autoCompleteField = ({ ...props }) => {
    const {
        field,
        field: { name },
        form: {
            setFieldValue,
            touched,
            setTouched,
            errors
        },
        label
    } = props;

    return (
        <>
            <Autocomplete
                {...props}
                {...field}
                renderInput={params => <TextField {...params} label={label} />}
                onChange={(_, value) => setFieldValue(name, value)}
                onBlur={() => setTouched({ [name]: true })}
            />

            {touched[field.name] && errors[field.name] && (
                <small className="handle-text-error handle-error-animation">
                    {errors[field.name]}
                </small>
            )}
        </>
    );
};

export const textArea = ({
    field,
    form: { touched, errors },
    rows,
    noVariant,
    ...props
}) => {
    return (
        <>
            <TextField
                {...field}
                {...props}
                fullWidth
                multiline
                minRows={rows}
                variant={noVariant ? undefined : "outlined"}
            />

            {touched[field.name] && errors[field.name] && (
                <small className="handle-text-error handle-error-animation">
                    {errors[field.name]}
                </small>
            )}
        </>
    );
};

export const SelectField = ({ ...props }) => {
    const [isShrinked, setIsShrinked] = useState(false);
    const [selectedValue, setSelectedValue] = useState(null);

    const {
        field,
        field: { name },
        form: {
            setFieldValue,
            touched,
            errors
        },
        label,
        width,
        textFieldProps,
        style,
        onChange,
        onInputChange
    } = props;

    useEffect(() => {
        setIsShrinked(field.value ? true : false);
        setSelectedValue(field.value);
    }, [field.value]);

    return (
        <div style={{ width: width }}>
            <ReactSelectForm
                {...field}
                {...props}
                textFieldProps={{
                    InputLabelProps: {
                        shrink: isShrinked
                    },
                    label,
                    // donot remove this line of code below
                    style: { marginTop: "-5px" },
                    ...textFieldProps
                }}
                style={style}
                onChange={value => {
                    setFieldValue(name, value);

                    if (value === null) {
                        setIsShrinked(false);
                        setSelectedValue(null);
                    } else {
                        setIsShrinked(true);
                        setSelectedValue(value);
                    }

                    onChange && onChange(value, setFieldValue);
                }}
                onInputChange={value => {
                    if (
                        value === "" ||
                        value === null ||
                        value === undefined
                    ) {
                        setIsShrinked(selectedValue ? true : false);
                    } else {
                        setIsShrinked(true);
                    }

                    onInputChange && onInputChange(value);
                }}
            />

            {touched[field.name] && errors[field.name] && (
                <small className="handle-text-error handle-error-animation">
                    {errors[field.name].value}
                </small>
            )}
        </div>
    );
};

// DONOT remove this block of method below, this is the legacy/older version of "selectField" component, before fixing the overlap label issue!
export const selectField = ({ ...props }) => {
    const {
        field,
        field: { name },
        form: {
            touched,
            setFieldValue,
            errors
        },
        label,
        width,
        textFieldProps,
        style,
        onChange
    } = props;

    let isShrinked = field.value ? true : false;

    return (
        <div style={{ width: width }}>
            <ReactSelectForm
                {...field}
                {...props}
                textFieldProps={{
                    InputLabelProps: {
                        shrink: isShrinked
                    },
                    label,
                    // donot remove this line of code below
                    style: { marginTop: "-5px" },
                    ...textFieldProps
                }}
                style={style}
                onChange={value => {
                    setFieldValue(name, value);
                    onChange && onChange(value, setFieldValue);
                }}
            />

            {touched[field.name] && errors[field.name] && (
                <small className="handle-text-error handle-error-animation">
                    {errors[field.name]}
                </small>
            )}
        </div>
    );
};
// END OF legacy/older version of "selectField" component, before fixing the overlap label issue !

export const selectFieldWithOutFormik = props => {
    return (
        <div style={{ width: props.width }}>
            <ReactSelectForm
                {...props}
                style={props.style}
            />
        </div>
    );
};

export const datePicker = ({
    field: { name, value },
    form: {
        setFieldValue,
        errors,
        // setFieldError
    },
    style,
    ...props
}) => {
    const currentError = errors[name];

    // let DefaultValue = value !== null
    //     ? value
    //     : moment();

    let disabledToolbar = props.disabledToolbar ? true : false;

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
                variant="inline"
                autoOk
                name={name}
                value={value}
                format="dd/MM/yyyy"
                helperText={currentError}
                disableToolbar={disabledToolbar}
                style={style}
                error={Boolean(currentError)}
                // onError={(error) => {
                //     // // handle as a side effect
                //     // if (error !== currentError) {
                //     //     setFieldError(name, error);
                //     // }
                // }}
                // if you are using custom validation schema you probably want to pass `true` as third argument
                onChange={date => setFieldValue(name, date, false)}
                {...props}
            />
        </MuiPickersUtilsProvider>
    );
};

export const timePicker = ({
    form: {
        setFieldValue,
        errors,
        setFieldError
    },
    field: { name, value },
    ...other
}) => {
    const currentError = errors[name];

    let DefaultValue = value || moment();

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardTimePicker
                name={name}
                value={DefaultValue}
                ampm={false}
                format="HH:mm"
                // mask="__:__ _M"
                helperText={currentError}
                error={Boolean(currentError)}
                // if you are using custom validation schema you probably want to pass `true` as third argument
                onChange={date => setFieldValue(name, date, false)}
                onError={error => {
                    // handle as a side effect
                    if (error !== currentError) {
                        setFieldError(name, error);
                    }
                }}
                {...other}
            />
        </MuiPickersUtilsProvider>
    );
};

export const switchField = ({
    field,
    field: { name, value },
    form: { touched, errors },
    label,
    ...props
}) => {
    return (
        <>
            <FormControlLabel
                label={label}
                control={
                    <Switch
                        {...field}
                        {...props}
                        color="primary"
                        checked={value}
                    />
                }
            />

            {touched[name] && errors[name] && (
                <small className="handle-text-error handle-error-animation">
                    {errors[name]}
                </small>
            )}
        </>
    );
};

export const SelectComponent = ({
    field,
    field: { name },
    fieldId,
    fieldName,
    options,
    form: {
        touched,
        setFieldValue,
        errors
    },
    ...props
}) => {
    return (
        <>
            <Select
                {...field}
                {...props}
                fullWidth
                onChange={e => setFieldValue(name, e.target.value)}
            >
                {options.map((item, index) => (
                    <Tooltip title={item[fieldName]} key={index}>
                        <MenuItem value={item[fieldId]}>
                            {item[fieldName]}
                        </MenuItem>
                    </Tooltip>
                ))}
            </Select>

            {touched[name] && errors[name] && (
                <div className="handle-text-error handle-error-animation">
                    {errors[name]}
                </div>
            )}
        </>
    );
};

export const MultiSelectComponent = ({
    field: { name, value },
    form: {
        setFieldValue,
        touched,
        errors
    },
    label,
    options,
    placeholder,
    limit
}) => {
    const icon = <CheckBoxOutlineBlankIcon color="primary" fontSize="small" />;
    const checkedIcon = <CheckBoxIcon color="primary" fontSize="small" />;

    return (
        <>
            <Autocomplete
                multiple
                id="checkbox-select"
                options={options}
                getOptionLabel={option => option[label].toString()}
                getOptionSelected={(option, value) =>
                    option[label].toString() === value[label].toString()
                }
                limittags={limit || 3}
                value={value}
                disableCloseOnSelect
                filterSelectedOptions
                // includeInputInList
                renderInput={params => (
                    <TextField
                        {...params}
                        variant="outlined"
                        label={placeholder}
                        placeholder={value.length > 0 ? "" : placeholder}
                    />
                )}
                renderOption={(option, { selected }) => (
                    <>
                        <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                        />
                        {option[label]}
                    </>
                )}
                // renderValue={selected => selected.join(", ")}
                onChange={(e, value) => setFieldValue(name, value)}
            />

            {touched[name] && errors[name] && (
                <div className="handle-text-error handle-error-animation">
                    {errors[name]}
                </div>
            )}
        </>
    );
};

export const DatePickerComponent = ({ ...props }) => {
    const {
        field,
        field: { name, value },
        form: { errors, setFieldValue },
        style,
        onChange
    } = props;

    const isDate = moment(value).isAfter("1900-01-01");
    const currentError = errors[name];

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
                {...field}
                {...props}
                autoOk
                variant="inline"
                fullWidth
                // Hendri comment out this line of code below because of field alignment issue on 18th November 2020
                // margin="normal"
                name={name}
                value={isDate ? value : null}
                format="dd MMM yyyy"
                style={style}
                error={Boolean(currentError)}
                helperText={currentError}
                onChange={date => {
                    setFieldValue(name, date);
                    onChange && onChange(date, setFieldValue);
                }}
            />
        </MuiPickersUtilsProvider>
    );
};

export const YearPicker = ({ ...props }) => {
    const {
        field,
        field: { name, value },
        form: { setFieldValue, errors },
        style,
        onChange
    } = props;

    const isDate = moment(value).isAfter("1900-01-01");
    const currentError = errors[name];

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
                allowKeyboardControl
                {...field}
                {...props}
                variant="inline"
                openTo="year"
                views={["year"]}
                name={name}
                value={isDate ? value : null}
                format="yyyy"
                autoOk
                clearable
                fullWidth
                style={style}
                error={Boolean(currentError)}
                helperText={currentError}
                onChange={date => {
                    setFieldValue(name, date);
                    onChange && onChange(date, setFieldValue);
                }}
            />
        </MuiPickersUtilsProvider>
    );
};

export const checkboxField = ({
    field,
    field: { name },
    form: { touched, errors },
    label,
    ...props
}) => {
    return (
        <>
            <FormControlLabel
                label={label}
                control={
                    <Checkbox
                        {...field}
                        {...props}
                        color="primary"
                    />
                }
            />

            {touched[name] && errors[name] && (
                <small className="handle-text-error handle-error-animation">
                    {errors[name]}
                </small>
            )}
        </>
    );
};

export const passwordField = ({
    field,
    field: { name },
    form: { touched, errors },
    label,
    showPassword,
    handleClickShowPassword,
    handleMouseDownPassword,
    ...props
}) => (
    <>
        <label
            style={{
                color: "rgba(0, 0, 0, 0.54)",
                padding: 0,
                fontSize: "12px",
                fontFamily: "poppins",
                fontWeight: 400,
                lineHeight: 1
            }}
        >
            {label}
        </label>

        <Input
            {...field}
            {...props}
            fullWidth
            id="standard-adornment-password"
            type={showPassword ? "text" : "password"}
            endAdornment={
                <InputAdornment position="end">
                    <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                    >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                </InputAdornment>
            }
        />

        {touched[name] && errors[name] && (
            <small className="handle-text-error handle-error-animation">
                {errors[name]}
            </small>
        )}
    </>
);